import { Combobox, ComboboxInput, FormValue, RadioboxGroup, SimpleRadiobox, TextInput } from '@startlibs/form'
import { FormattedMessage, useIntl } from 'react-intl'
import { Icon, SplitColumnsContainer } from '@startlibs/components';
import { getColor, smoothScroll } from '@startlibs/utils';
import React, { useEffect, useRef } from 'react'
import styled from 'styled-components';
import { AFTERNOON, EVENING, MORNING } from '../../enums/ContactTime'
import { CONTACT_RELATION_LEGAL_LABELS, CONTACT_RELATION_LEGAL_OPTIONS, INTL_CONTACT_RELATION_MEDICAL_LABELS, LOCAL_PHYSICIAN, OTHER, PATIENT, REF_PHYSICIAN } from '../../enums/ContactRelationType'
import { Card, SectionHeading } from '../../components/PageLayout';
import { ConfirmEmailInput } from '../../components/ConfirmEmailInput'
import { EMAIL, PHONE, TEXT } from '../../enums/ContactCommunicationType'
import { IfLocale } from '../../hocs/IfLocale'
import { getFormattedStates } from '../PatientAndContactDetails';
import StyledTelInput from '../../components/StyledTelInput';
import { lazyProviderInfo } from '../../components/WithProvider';
import {LocaleOption} from "../../components/LocaleSelector";

export const AddressHeading = styled.div`
  font-size: 13px;
  color: ${getColor('gray150')};
  padding-bottom: .25rem;
  border-bottom: 1px solid ${getColor('gray210')};
  margin: 1.5rem 0;
`

export const ComboboxContainer = styled.div`
  ${props => props.readOnly && `
    ${ComboboxInput} {
      background: rgba(0,0,0,0.075) !important;
      opacity: 0.75 !important;
      pointer-events: none !important;
      ${Icon} {
        opacity: 0.4;
      }
    }
  `}
`

const VIEWPORT_MIN_WIDTH = 500

// export const getLabelForLocale 
export const ContactDetails = ({ readOnly, scrollTo, form, countryList, showSelects, isPatientCreating=false, caseRequest,
    contactPhoneNumberValid, setContactPhoneNumberValid,
    contactFaxNumberValid, setContactFaxNumberValid
  }) => {

  const headerRef = useRef()
  const intl = useIntl()
  const providerInfo = lazyProviderInfo.read()
  
  useEffect(() => {
    if (scrollTo) {
      smoothScroll({ target: headerRef.current, offset: -90 })
    }
  }, [])

  return <>
    <SectionHeading ref={headerRef}>
      <h3><FormattedMessage
        description='ContactDetails title'
        defaultMessage='Contact party'
      /></h3>
      <p>
        <FormattedMessage
          description='ContactDetails subtitle'
          defaultMessage="This is the party responsible for this case. They may be contacted about patient information, medical records, payment, and the case report as applicable."
        />
      </p>
    </SectionHeading>
    <Card css="padding-bottom:1.5rem;">
      <IfLocale not contains="LEGAL">
        <ComboboxContainer readOnly={readOnly} className='fs-exclude'><Combobox
          disabled={readOnly}
          className="combobox fs-exclude"
          // label={intl.formatMessage({ defaultMessage: "Select relationship to the patient", description: "ContactDetails contactRelationType label" })}
          // path='contactDetails.contactRelationType'
          // options={CONTACT_RELATION_MEDICAL_OPTIONS}
          // getLabel={v => CONTACT_RELATION_MEDICAL_LABELS[v]}
          label={intl.formatMessage({ defaultMessage: "Select relationship to the patient", description: "ContactDetails contactRelationType label" })}
          path='contactDetails.contactRelationType'
          options={Object.keys(INTL_CONTACT_RELATION_MEDICAL_LABELS(intl))}
          getLabel={v => INTL_CONTACT_RELATION_MEDICAL_LABELS(intl)[v]}
          mandatory
        /></ComboboxContainer>
      </IfLocale>

      <IfLocale contains="LEGAL">
        <SplitColumnsContainer viewportMinWidth={VIEWPORT_MIN_WIDTH} margin="big">
          <ComboboxContainer readOnly={readOnly} className='fs-exclude'><Combobox
            disabled={readOnly}
            className="combobox fs-exclude"
            label={intl.formatMessage({ defaultMessage: "Select relationship to the patient", description: "ContactDetails contactRelationType label" })}
            path='contactDetails.contactRelationType'
            options={CONTACT_RELATION_LEGAL_OPTIONS}
            getLabel={v => CONTACT_RELATION_LEGAL_LABELS[v]}
            mandatory
          /></ComboboxContainer>
        </SplitColumnsContainer>
      </IfLocale>

      <FormValue path="contactDetails.contactRelationType">{relationtype =>
        relationtype && relationtype !== PATIENT && <>
          <SplitColumnsContainer viewportMinWidth={VIEWPORT_MIN_WIDTH} margin="big">
            <TextInput
              disabled={readOnly}
              type="text"
              path="contactDetails.fullName.first"
              label={intl.formatMessage({ defaultMessage: "Your first name", description: "ContactDetails fullName.first label" })}
              placeholder={intl.formatMessage({ defaultMessage: "Ex: John", description: "ContactDetails fullName.first placeholder" })}
              maxLength={255}
              mandatory
            />
            <TextInput
              disabled={readOnly}
              type="text"
              path="contactDetails.fullName.last"
              label={intl.formatMessage({ defaultMessage: "Your last name", description: "ContactDetails fullName.last label" })}
              placeholder={intl.formatMessage({ defaultMessage: "Ex: Doe", description: "ContactDetails fullName.last placeholder" })}
              maxLength={255}
              mandatory
            />
            {(relationtype === REF_PHYSICIAN || relationtype === LOCAL_PHYSICIAN) && <TextInput
              disabled={readOnly}
              type="text"
              path="contactDetails.npi"
              label={intl.formatMessage({ defaultMessage: "NPI", description: "ContactDetails npi label" })}
              placeholder={intl.formatMessage({ defaultMessage: "Ex: 1234567890", description: "ContactDetails npi placeholder" })}
              maxLength={255}
            // mandatory
            />}
          </SplitColumnsContainer>
          {relationtype === OTHER &&
            <TextInput
              disabled={readOnly}
              type="text"
              path="contactDetails.otherRelationType"
              label={intl.formatMessage({ defaultMessage: "Specify your relationship to the patient", description: "ContactDetails otherRelationType label" })}
              // label="Specify your relation with the patient"
              // placeholder="Ex: Father"
              maxLength={255}
              mandatory
            />
          }
        </>}</FormValue>

      {/* IF IS REF_PHYSICIAN */}
      <FormValue path="contactDetails.contactRelationType">{relationtype =>
        relationtype && (relationtype === REF_PHYSICIAN || relationtype === LOCAL_PHYSICIAN) && <>
          <SplitColumnsContainer viewportMinWidth={VIEWPORT_MIN_WIDTH} margin="big">
            {showSelects ? <>
              <ComboboxContainer readOnly={readOnly} className='fs-exclude'><Combobox
                disabled={readOnly}
                className="combobox fs-exclude"
                label={intl.formatMessage({ defaultMessage: "Country", description: "PatientDetails country label" })}
                placeholder={intl.formatMessage({ defaultMessage: "Please select country", description: "PatientDetails country select placeholder" })}
                path='contactDetails.address.country'
                options={countryList}
                onChange={(val) => {
                  var states = getFormattedStates(val)
                  if(states.length > 0){
                    if(states.findIndex(x => x === form.getValue('contactDetails.address.state')) == -1){
                      form.setValue('contactDetails.address.state', null)
                    }
                  }
                }}
              /></ComboboxContainer>
              <FormValue path="contactDetails.address.country">{country => 
                <ComboboxContainer readOnly={readOnly} className='fs-exclude'>
                  <Combobox
                    disabled={readOnly}
                    className="combobox fs-exclude"
                    label={intl.formatMessage({ defaultMessage: "State/Province", description: "PatientDetails state label" })}
                    placeholder={intl.formatMessage({ defaultMessage: "Please select state", description: "PatientDetails state select placeholder" })}
                    path='contactDetails.address.state'
                    options={getFormattedStates(country)}
                    // onChange={(val) => setRegion(val)}
                  />
                </ComboboxContainer>
              }</FormValue>
            </> : <>
              <TextInput
                disabled={readOnly}
                type="text"
                path="contactDetails.address.country"
                label={intl.formatMessage({ defaultMessage: "Country", description: "PatientDetails country label" })}
                placeholder={intl.formatMessage({ defaultMessage: "Ex: USA", description: "PatientDetails country placeholder" })}
                maxLength={255}
              // mandatory
              />
              <TextInput
                disabled={readOnly}
                type="text"
                path="contactDetails.address.state"
                label={intl.formatMessage({ defaultMessage: "State", description: "PatientDetails state label" })}
                placeholder={intl.formatMessage({ defaultMessage: "Ex: New York", description: "PatientDetails state placeholder" })}
                maxLength={255}
              // mandatory
              />
            </>
            }
          </SplitColumnsContainer>

          <SplitColumnsContainer viewportMinWidth={VIEWPORT_MIN_WIDTH} margin="big">
            <TextInput
              disabled={readOnly}
              type="text"
              path="contactDetails.address.city"
              label={intl.formatMessage({ defaultMessage: "City", description: "PatientDetails city label" })}
              placeholder={intl.formatMessage({ defaultMessage: "Ex: New York", description: "PatientDetails city placeholder" })}
              maxLength={255}
            // mandatory
            />
            <TextInput
              disabled={readOnly}
              type="text"
              path="contactDetails.address.zipCode"
              label={intl.formatMessage({ defaultMessage: "Postal code", description: "PatientDetails postal code label" })}
              placeholder={intl.formatMessage({ defaultMessage: "Ex: 12345", description: "PatientDetails postal code placeholder" })}
              maxLength={255}
            // mandatory
            />
          </SplitColumnsContainer>
          <TextInput
            disabled={readOnly}
            type="text"
            path="contactDetails.address.address"
            label={intl.formatMessage({ defaultMessage: "Street address - Line 1", description: "PatientDetails street address line 1 label" })}
            placeholder={intl.formatMessage({ defaultMessage: "Ex: 001 Street Name", description: "PatientDetails street address line 1 placeholder" })}
            maxLength={255}
          // mandatory
          />

        </>}
      </FormValue>
      {/* END IF IS REF_PHYSICIAN */}

      <SplitColumnsContainer viewportMinWidth={VIEWPORT_MIN_WIDTH} margin="big">
        <TextInput
          disabled={readOnly}
          type="text"
          path="contactDetails.email"
          label={intl.formatMessage({ defaultMessage: "Email address", description: "ContactDetails email label" })}
          placeholder={intl.formatMessage({ defaultMessage: "Ex: your@email.com", description: "ContactDetails email placeholder" })}
          maxLength={255}
          mandatory
        />

        <ConfirmEmailInput
          disabled={readOnly}
          type="text"
          confirmingPath="contactDetails.email"
          path="contactDetails.confirmEmail"
          label={intl.formatMessage({ defaultMessage: "Confirm email", description: "ContactDetails confirm email label" })}
          placeholder={intl.formatMessage({ defaultMessage: "Ex: your@email.com", description: "ContactDetails confirm email placeholder" })}
          maxLength={255}
          mandatory
        />
      </SplitColumnsContainer>
      <FormValue path="contactDetails.contactRelationType">{relationtype => <>
        {relationtype && (relationtype === REF_PHYSICIAN || relationtype === LOCAL_PHYSICIAN) && <>
          <FormValue path="contactDetails.address.country">{_physicianCountry =>
            <SplitColumnsContainer viewportMinWidth={VIEWPORT_MIN_WIDTH} margin="big">
              <TextInput
                disabled={readOnly}
                type="text"
                path="contactDetails.institution"
                label={intl.formatMessage({ defaultMessage: "Institution", description: "ContactDetails institution label" })}
                placeholder={intl.formatMessage({ defaultMessage: "Ex: Purview", description: "ContactDetails institution placeholder" })}
                maxLength={255}
              />
              <StyledTelInput
                path="contactDetails.faxNumber"
                hasErrors={form.hasErrors()}
                disabled={readOnly}
                label={intl.formatMessage({ defaultMessage: "Fax number", description: "ContactDetails fax number label" })}
                placeholder={intl.formatMessage({ defaultMessage: "(201) 555-0123", description: "ContactDetails fax number placeholder" })}
                initOptions={{
                  initialCountry: "us",
                  separateDialCode: false,
                  autoPlaceholder: "aggressive",
                  formatOnDisplay: true,
                  placeholderNumberType: "FIXED_LINE_OR_MOBILE",
                  nationalMode: false,
                  countryOrder: ["us"]
                }}
                tabIndex={1}
                isValid={contactFaxNumberValid}
                setIsValid={setContactFaxNumberValid}
              />
            </SplitColumnsContainer>
          }</FormValue>  
        </>}
        
          
            <SplitColumnsContainer viewportMinWidth={VIEWPORT_MIN_WIDTH} margin="big">
              <StyledTelInput
                mandatory 
                path="contactDetails.phoneNumber"
                hasErrors={form.hasErrors()}
                label={intl.formatMessage({ defaultMessage: "Phone number", description: "ContactDetails phone number label" })}
                placeholder={intl.formatMessage({ defaultMessage: "(201) 555-0123", description: "ContactDetails phone number placeholder" })}
                disabled={readOnly}
                initOptions={{
                  initialCountry: "us",
                  separateDialCode: false,
                  autoPlaceholder: "aggressive",
                  formatOnDisplay: true,
                  placeholderNumberType: "FIXED_LINE_OR_MOBILE",
                  nationalMode: false,
                  countryOrder: ["us"],
                  validationNumberType: "FIXED_LINE_OR_MOBILE"
                }}
                tabIndex={1}
                isValid={contactPhoneNumberValid}
                setIsValid={setContactPhoneNumberValid}
              />
              <RadioboxGroup disabled={readOnly} mandatory 
                label={intl.formatMessage({ defaultMessage: "Preferred mode of communication", description: "ContactDetails prefCommunicationMode label" })}
                horizontal path="contactDetails.prefCommunicationMode" framedBoxes wide>
                <SimpleRadiobox fieldValue={PHONE} label={intl.formatMessage({ defaultMessage: "Phone", description: "ContactDetails prefCommunicationMode phone label" })} className='fs-exclude' />
                            <SimpleRadiobox fieldValue={EMAIL} label={intl.formatMessage({ defaultMessage: "Email", description: "ContactDetails prefCommunicationMode email label" })} className='fs-exclude' />
                {/* {(!isPatientCreating && (mode === TEXT || !mode)) && <SimpleRadiobox fieldValue={TEXT} label="Text" className='fs-exclude' />} */}
                {(!isPatientCreating && (caseRequest?.contactDetails?.prefCommunicationMode === TEXT)) && <SimpleRadiobox fieldValue={TEXT} label={intl.formatMessage({ defaultMessage: "Text", description: "ContactDetails prefCommunicationMode text label" })} className='fs-exclude' />}
              </RadioboxGroup>
            </SplitColumnsContainer> 
        
      </>}</FormValue>
      <RadioboxGroup disabled={readOnly} 
        label={intl.formatMessage({ defaultMessage: "Preferred contact time", description: "ContactDetails prefContactTime label" })}
        horizontal path="contactDetails.prefContactTime" framedBoxes wide
      >
        <SimpleRadiobox fieldValue={MORNING} label={intl.formatMessage({ defaultMessage: "Morning", description: "ContactDetails prefContactTime morning label" })} className='fs-exclude' />
        <SimpleRadiobox fieldValue={AFTERNOON} label={intl.formatMessage({ defaultMessage: "Afternoon", description: "ContactDetails prefContactTime afternoon label" })} className='fs-exclude' />
        <SimpleRadiobox fieldValue={EVENING} label={intl.formatMessage({ defaultMessage: "Evening", description: "ContactDetails prefContactTime evening label" })} className='fs-exclude' />
      </RadioboxGroup>
      {/* Flags Examples:
        <div class="iti__flag iti__gb"/>
        <div class="iti__flag iti__fr"/>
        <div class="iti__flag iti__us"/>
        <div class="iti__flag iti__ca"/>
       */}
      {providerInfo?.preferredLanguage?.options?.length > 1 && <ComboboxContainer readOnly={readOnly} className='fs-exclude'>
                <Combobox
                  disabled={readOnly}
                  path="caseInfo.preferredLanguage"
                  className="combobox fs-exclude"
                  label={intl.formatMessage({ defaultMessage: "Preferred language", description: "ContactDetails preferred language label" })}
                  options={providerInfo?.preferredLanguage?.options.map(o => o.id)}
                  getLabelAs={v => <LocaleOption locale={v} providerInfo={providerInfo}/>}
                  getBoxLabelAs={v => <LocaleOption locale={v} providerInfo={providerInfo}/>}
              /></ComboboxContainer>}

        
    </Card>
  </>
}
